import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useMemo, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { invoiceGridSortFieldToApi, normalizeInvoicesSortColumn } from '../constants/invoicesSortableColumns';
import { useInvoices } from './useInvoices';

const fieldToApiMapping: Record<string, string> = {
  erp_number: 'erp_number',
  date: 'date',
  grand_total: 'grand_total',
};

export const useInvoicesMain = () => {
  const { params: queryParams, updateParams: setQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] },
  });
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.search as string);
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? (queryParams.filters as FilterConditionOutput[]) : [],
  );
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'id',
    direction: 'desc',
  });

  const apiSortBy = useMemo(
    () => normalizeInvoicesSortColumn(invoiceGridSortFieldToApi(sorting.field)),
    [sorting.field],
  );

  const searchFilter = useMemo(() => {
    if (!filterConditions || filterConditions.length === 0) {
      return undefined;
    }

    const filters: any[] = [];

    filterConditions.forEach((condition) => {
      if (!condition.action) {
        return;
      }

      const apiField = fieldToApiMapping[condition.column] || condition.column;
      const operator = condition.action;

      if (operator === 'is_null' || operator === 'is_not_null') {
        filters.push({
          column: apiField,
          operator,
        });
        return;
      }

      if (condition.value === undefined || condition.value === null || condition.value === '') {
        return;
      }

      let value = condition.value;
      if (operator === 'in' || operator === 'not_in') {
        if (typeof value === 'string') {
          value = value
            .split(',')
            .map((item) => String(item).trim())
            .filter(Boolean);
        } else if (!Array.isArray(value)) {
          value = [value];
        }

        if (!Array.isArray(value) || value.length === 0) {
          return;
        }
      }

      filters.push({
        column: apiField,
        operator,
        value,
      });
    });

    if (filters.length === 0) {
      return undefined;
    }

    return {
      filters,
      logic: 'AND',
    };
  }, [filterConditions]);

  const normalizedSearch = useMemo(() => {
    const value = searchTerm?.trim();
    return value ? value : undefined;
  }, [searchTerm]);

  const { invoices, isLoading, isFetching, error, total, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInvoices({
      searchFilter,
      search: normalizedSearch,
      sortBy: apiSortBy,
      sortDirection: sorting.direction,
    });

  const hasActiveFilters = useMemo(() => filterConditions.length > 0, [filterConditions]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      setQueryParams({
        filters: conditions,
      });
    },
    [setQueryParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setQueryParams({
        search: value,
      });
    },
    [setQueryParams],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    setSelectedColumns(columns);
  }, []);

  const handleSortingChange = useCallback((next: { field: string; direction: 'asc' | 'desc' } | null) => {
    if (!next) {
      setSorting({ field: 'id', direction: 'desc' });
      return;
    }
    setSorting(next);
  }, []);

  return {
    invoices,
    isLoading,
    isFetching,
    error,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
    filterConditions,
    searchTerm,
    selectedColumns,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    sorting,
    handleSortingChange,
  };
};
